import { cn } from "@do/ui/lib/utils";
import { icons, LinkIcon, PhoneIcon, Undo2Icon } from "lucide-react";
import { useMemo } from "react";
import { ButtonStyles } from "../../../../constants";
import { useOptionalChat } from "../../../../providers/chat-provider";
import { type MessageButtonProps } from "./button";

type ButtonIconProps = {
  template: {
    iconName?: string;
    iconUrl?: string;
    style?: ButtonStyles;
  };
  buttonId: string | undefined;
  storeIcon?: React.ComponentType<{ id?: string; className?: string }>;
} & Partial<Pick<MessageButtonProps, "buttonType" | "isBackButton">>;

export const buttonIcons = {
  url: LinkIcon,
  tel: PhoneIcon,
};

// Drawn in the text colour, so they take the hover colour of the button holding them, the one with
// the `group/message-button` class.
const ICON_CLASS_NAME =
  "size-(--message-button-icon-size) text-(--message-button-icon-color) group-hover/message-button:text-(--message-button-hover-icon-color)";
const CTA_ICON_CLASS_NAME =
  "size-(--message-button-cta-icon-size) text-(--message-button-cta-icon-color) group-hover/message-button:text-(--message-button-cta-hover-icon-color)";

const isSvgIconUrl = (url: string) => {
  const lower = url.toLowerCase();
  if (lower.startsWith("data:image/svg+xml")) return true;
  const withoutQueryOrHash = lower.split("#")[0]?.split("?")[0] ?? lower;
  return withoutQueryOrHash.endsWith(".svg");
};

export const ButtonIcon = ({
  isBackButton,
  buttonType,
  template,
  buttonId,
  storeIcon: StoreIcon,
}: ButtonIconProps) => {
  const chatbotParams = useOptionalChat()?.chatbotParams;

  const Icon = useMemo(() => {
    if (isBackButton) {
      if (chatbotParams?.buttons?.backButtonIconName) {
        return icons?.[chatbotParams.buttons.backButtonIconName as keyof typeof icons];
      }
      return Undo2Icon;
    }
    if ("iconName" in template && template.iconName)
      return icons?.[template.iconName as keyof typeof icons];
    return buttonType && buttonType in buttonIcons
      ? buttonIcons[buttonType as keyof typeof buttonIcons]
      : null;
  }, [isBackButton, template, buttonType, chatbotParams?.buttons?.backButtonIconName]);

  const iconUrl = useMemo(() => {
    if (isBackButton && chatbotParams?.buttons?.backButtonIconUri) {
      return chatbotParams.buttons.backButtonIconUri;
    }
    if ("iconUrl" in template && template.iconUrl) return template.iconUrl;
    return undefined;
  }, [isBackButton, template, chatbotParams?.buttons?.backButtonIconUri]);

  return iconUrl ? (
    isSvgIconUrl(iconUrl) ? (
      <span
        id={`message-${buttonId}-button-icon-image`}
        data-message-button-icon-image="true"
        aria-hidden="true"
        className={cn(
          "inline-block shrink-0",
          template.style === ButtonStyles.CallToAction ? CTA_ICON_CLASS_NAME : ICON_CLASS_NAME
        )}
        // use mask image to use the color of the text for the icon
        style={{
          WebkitMaskImage: `url(${iconUrl})`,
          maskImage: `url(${iconUrl})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
          WebkitMaskSize: "contain",
          maskSize: "contain",
          backgroundColor: "currentColor",
        }}
      />
    ) : (
      <img
        id={`message-${buttonId}-button-icon-image`}
        src={iconUrl}
        className={cn(
          "object-contain shrink-0",
          template.style === ButtonStyles.CallToAction
            ? "size-(--message-button-cta-icon-image-size) text-(--message-button-cta-icon-color)"
            : "size-(--message-button-icon-image-size) text-(--message-button-icon-color)"
        )}
        aria-hidden="true"
        alt=""
      />
    )
  ) : Icon ? (
    <Icon
      id={`message-${buttonId}-button-icon`}
      aria-hidden="true"
      className={cn(
        "shrink-0",
        StoreIcon
          ? "size-(--message-store-buttons-icon-size) text-(--message-store-buttons-icon-color)"
          : template.style === ButtonStyles.CallToAction
          ? CTA_ICON_CLASS_NAME
          : ICON_CLASS_NAME
      )}
    />
  ) : StoreIcon ? (
    <StoreIcon
      id={`message-${buttonId}-button-icon-store`}
      className="size-(--message-store-buttons-icon-size) text-(--message-store-buttons-icon-color)"
      aria-hidden="true"
    />
  ) : null;
};
